import { Button, Group, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconEyeOff } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, setImpersonationToken } from '../lib/api.js';

/**
 * Impossible to miss on purpose.
 *
 * Someone who forgets they are viewing as a dealership will misread every page
 * they look at, so this sits above everything, in a colour nothing else uses,
 * for the whole thirty minutes.
 */
export function ImpersonationBanner({ target }: { target: string }) {
  const qc = useQueryClient();
  const [stopping, setStopping] = useState(false);

  async function stop() {
    setStopping(true);
    try {
      await api.del('/admin/impersonate');
    } catch {
      // Ending it locally matters more than the server round trip: the token
      // expires by itself, and being stuck in someone else's view is worse.
    }
    setImpersonationToken(null);
    // Stopped from inside the dealer portal: yourself again has no business
    // there, so go back to where a dealership view was started from.
    if (window.location.pathname.startsWith('/portal')) {
      window.location.href = '/app/dealers';
      return;
    }
    // Everything on screen was fetched as the other person.
    await qc.invalidateQueries();
    notifications.show({ color: 'green', message: 'You are yourself again' });
    setStopping(false);
  }

  return (
    <Group
      justify="center"
      gap="sm"
      py={6}
      px="md"
      style={{
        background: 'var(--mantine-color-orange-filled)',
        color: 'var(--mantine-color-white)',
      }}
    >
      <IconEyeOff size={16} />
      <Text size="sm" fw={600}>
        Viewing as {target}
      </Text>
      <Button
        size="compact-xs"
        variant="white"
        color="orange"
        loading={stopping}
        onClick={() => void stop()}
      >
        Stop
      </Button>
    </Group>
  );
}
